import {
  Archive,
  ArchiveRestore,
  ChevronLeft,
  ChevronRight,
  MoreHorizontal,
  Pencil,
  Plus,
  Search,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { clearComboboxCache } from '@/components/combobox';
import { ProjectDialog, type ProjectValues } from '@/components/project-dialog';
import { ResponsiveTable } from '@/components/responsive-table';
import { RowThumb } from '@/components/row-thumb';
import { ListPagination, ScrollArea } from '@/components/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useProjects } from '@/lib/api-hooks';
import { projectFilters, projectStatusStyles } from '@/lib/project-status';
import {
  type OrderKey,
  PROJECT_PAGE_SIZE_OPTIONS,
  type Project,
  archiveProject,
  createProject,
  restoreProject,
  updateProject,
} from '@/lib/projects';
import { useProjectsStore } from '@/lib/projects-store';
import { invalidateResource } from '@/lib/resource';
import { cn } from '@/lib/utils';

const orderOptions: Array<{ value: OrderKey; label: string }> = [
  { value: 'newest', label: 'Recently updated' },
  { value: 'name', label: 'Name (A–Z)' },
  { value: 'votes-desc', label: 'Most votes' },
  { value: 'votes-asc', label: 'Fewest votes' },
];

export function ProjectsPage() {
  const search = useProjectsStore((state) => state.search);
  const status = useProjectsStore((state) => state.status);
  const order = useProjectsStore((state) => state.order);
  const page = useProjectsStore((state) => state.page);
  const pageSize = useProjectsStore((state) => state.pageSize);
  const setSearch = useProjectsStore((state) => state.setSearch);
  const setStatus = useProjectsStore((state) => state.setStatus);
  const setOrder = useProjectsStore((state) => state.setOrder);
  const setPage = useProjectsStore((state) => state.setPage);
  const setPageSize = useProjectsStore((state) => state.setPageSize);

  const { data, isLoading } = useProjects({ search, status, order, page, pageSize });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Project | null>(null);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  function openCreate() {
    setEditing(null);
    setDialogOpen(true);
  }

  function openEdit(project: Project) {
    setEditing(project);
    setDialogOpen(true);
  }

  async function handleSubmit(values: ProjectValues) {
    if (editing) {
      await updateProject(editing.id, values);
    } else {
      await createProject(values);
    }
    setPage(1);
    clearComboboxCache('projects');
    invalidateResource('project');
  }

  async function handleArchive(project: Project) {
    await archiveProject(project.id);
    clearComboboxCache('projects');
    invalidateResource('project');
  }

  async function handleRestore(project: Project) {
    await restoreProject(project.id);
    clearComboboxCache('projects');
    invalidateResource('project');
  }

  return (
    <div className="flex h-full flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="relative w-full sm:w-60">
          <Search className="absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search projects"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="pl-8"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="hidden text-muted-foreground text-sm sm:inline">Order by</span>
          <Select value={order} onValueChange={(value) => setOrder(value as OrderKey)}>
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {orderOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" onClick={openCreate}>
            <Plus />
            New project
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-1">
        {projectFilters.map((filter) => (
          <Button
            key={filter}
            size="sm"
            variant={status === filter ? 'default' : 'ghost'}
            onClick={() => setStatus(filter)}
            className="h-8"
          >
            {filter}
          </Button>
        ))}
      </div>

      <ScrollArea>
        <ResponsiveTable
          loading={isLoading}
          loadingLabel="Loading projects…"
          skeletonRows={pageSize}
          emptyLabel="No projects match your filters."
          rows={items}
          rowKey={(project) => project.id}
          columns={[
            {
              key: 'project',
              header: 'Project',
              title: true,
              cell: (project: Project) => (
                <div className="flex items-center gap-3">
                  <RowThumb media={project.heroMedia} label={project.name} />
                  <Link to={`/projects/${project.slug}`} className="font-medium hover:underline">
                    {project.name}
                  </Link>
                </div>
              ),
            },
            {
              key: 'status',
              header: 'Status',
              cell: (project: Project) => (
                <Badge variant="outline" className={cn(projectStatusStyles[project.status])}>
                  {project.status}
                </Badge>
              ),
            },
            {
              key: 'artists',
              header: 'Artists',
              align: 'right',
              cell: (project: Project) => project.artists,
            },
            {
              key: 'votes',
              header: 'Votes',
              align: 'right',
              cell: (project: Project) => project.votes.toLocaleString(),
            },
            {
              key: 'period',
              header: 'Voting period',
              secondary: true,
              cell: (project: Project) => (
                <span className="text-muted-foreground">{project.period}</span>
              ),
            },
            {
              key: 'updated',
              header: 'Updated',
              align: 'right',
              secondary: true,
              cell: (project: Project) => (
                <span className="text-muted-foreground">{project.updated}</span>
              ),
            },
          ]}
          actions={(project) => (
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger
                aria-label={`Actions for ${project.name}`}
                className="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
              >
                <MoreHorizontal className="size-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onSelect={() => openEdit(project)}>
                  <Pencil />
                  Edit
                </DropdownMenuItem>
                {project.status === 'Archive' ? (
                  <DropdownMenuItem onSelect={() => handleRestore(project)}>
                    <ArchiveRestore />
                    Restore
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem onSelect={() => handleArchive(project)}>
                    <Archive />
                    Archive
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        />
        {(total > 0 || isLoading) && (
          <ListPagination>
            <p className="text-muted-foreground text-sm">
              {isLoading ? 'Loading…' : `Showing ${start}–${end} of ${total}`}
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground text-sm">Rows per page</span>
                <Select
                  value={String(pageSize)}
                  onValueChange={(value) => setPageSize(Number(value))}
                >
                  <SelectTrigger size="sm" className="w-[4.5rem]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PROJECT_PAGE_SIZE_OPTIONS.map((size) => (
                      <SelectItem key={size} value={String(size)}>
                        {size}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8"
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1 || isLoading}
                >
                  <ChevronLeft />
                  Previous
                </Button>
                {Array.from({ length: totalPages }, (_, index) => index + 1).map((number) => (
                  <Button
                    key={number}
                    variant={number === page ? 'default' : 'ghost'}
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => setPage(number)}
                    disabled={isLoading}
                  >
                    {number}
                  </Button>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8"
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages || isLoading}
                >
                  Next
                  <ChevronRight />
                </Button>
              </div>
            </div>
          </ListPagination>
        )}
      </ScrollArea>

      {dialogOpen && (
        <ProjectDialog
          open
          onOpenChange={(next) => {
            if (!next) setDialogOpen(false);
          }}
          mode={editing ? 'edit' : 'create'}
          initialValues={
            editing
              ? {
                  name: editing.name,
                  status: editing.status,
                  description: editing.description,
                  heroMedia: editing.heroMedia ?? null,
                  gallery: editing.gallery ?? [],
                }
              : undefined
          }
          onSubmit={handleSubmit}
        />
      )}
    </div>
  );
}
